import React, { useState } from 'react';
import { Box, Chip, LinearProgress, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, Description } from '@material-ui/icons';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ADMIN_CONSOLE_CERTIFICATE_TEMPLATES } from './admin-console-certificates.gql';
import {
  GetAdminConsoleCertificateTemplates,
  GetAdminConsoleCertificateTemplates_vm_certificateTemplates as CertificateTemplate,
} from './__generated__/GetAdminConsoleCertificateTemplates';
import { CertificateTemplateDialog } from './CertificateTemplateDialog';
import { CertificateTemplateMenuButton } from './CertificateTemplateMenu';
import {
  CertificateTemplateDraft,
  describeTemplate,
  draftFromTemplate,
  emptyTemplateDraft,
  toTemplateRequest,
} from './certificate-model';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      paddingBottom: 40,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
      [theme.breakpoints.down('sm')]: { paddingLeft: 16, paddingRight: 16 },
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      paddingBottom: 8,
      borderBottom: '1px solid #efefef',
    },
    headingTitle: { color: theme.color.grey.neutralBrand900, fontWeight: 600, fontSize: 20, paddingBottom: 5 },
    panel: {
      background: 'white',
      padding: '1.25rem 1.875rem',
      marginTop: '2.25rem',
      [theme.breakpoints.down('sm')]: { padding: '1rem' },
    },
    sectionTitle: { fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center' },
    sectionDescription: { fontSize: 14, color: '#808080' },
    row: { borderTop: '1px solid #EFEFEF', padding: '.6rem 1rem .6rem 0', marginTop: '1.25rem' },
    rowHead: { display: 'flex', alignItems: 'center', gap: '12px' },
    name: { fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0 },
    description: { fontSize: 14, color: '#808080', margin: '4px 0 8px' },
    chips: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
    chip: { borderRadius: '4px', fontSize: '11px', fontWeight: 500, color: '#333' },
  })
);

export const AdminConsoleCertificates = () => {
  const classes = useStyles();
  const { isSm } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const { data, loading, refetch } = useCampfireQuery<GetAdminConsoleCertificateTemplates, {}>(
    GET_ADMIN_CONSOLE_CERTIFICATE_TEMPLATES
  );
  const saveFetch = useEndpointFetch<ReturnType<typeof toTemplateRequest>>(
    '/vm/admin-console/certificate-template/save'
  );
  const deleteFetch = useEndpointFetch<{ certificateTemplateId: string }>(
    '/vm/admin-console/certificate-template/delete'
  );
  const [editorDraft, setEditorDraft] = useState<CertificateTemplateDraft>();
  const [deleting, setDeleting] = useState<CertificateTemplate>();

  const templates = data?.vm.certificateTemplates ?? [];

  const reload = () => {
    if (refetch) setTimeout(refetch, 0);
  };

  const onSave = (draft: CertificateTemplateDraft) =>
    saveFetch
      .run(toTemplateRequest(draft))
      .then((response) => {
        if (!response.ok) throw new Error(response.data?.message);
        setSnackbar({ open: true, variant: 'success', message: `${draft.name.trim()} saved` });
        setEditorDraft(undefined);
        reload();
      })
      .catch((e) => setSnackbar({ open: true, variant: 'error', message: e?.message || 'Unable to save template' }));

  const onDeleteConfirm = () => {
    if (!deleting) return;
    const { name, certificateTemplateId } = deleting;
    setDeleting(undefined);
    deleteFetch
      .run({ certificateTemplateId })
      .then((response) => {
        if (!response.ok) throw new Error();
        setSnackbar({ open: true, variant: 'success', message: `${name} deleted` });
        reload();
      })
      .catch(() => setSnackbar({ open: true, variant: 'error', message: 'Unable to delete template' }));
  };

  return (
    <Box className={classes.container}>
      <Box
        className={classes.heading}
        flexDirection={isSm ? 'column' : 'row'}
        alignItems={isSm ? 'flex-start' : 'center'}
      >
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Certificates
          </Typography>
          <Typography variant='body2'>
            Design certificates for achievement levels, or to issue to volunteers by hand
          </Typography>
        </Box>
        <ConsoleActionButton
          endIcon={<Add />}
          variant='contained'
          color='primary'
          onClick={() => setEditorDraft(emptyTemplateDraft())}
        >
          Add Template
        </ConsoleActionButton>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && templates.length === 0 ? (
        <Box className={classes.panel}>
          <Typography className={classes.sectionTitle}>
            <Description style={{ fontSize: 14, paddingRight: 5 }} /> Create your first certificate
          </Typography>
          <Typography variant='body2' className={classes.sectionDescription}>
            {
              "You haven't created any certificate templates yet. Click the 'Add Template' button top right to add your first."
            }
          </Typography>
        </Box>
      ) : (
        <Box className={classes.panel}>
          <Typography className={classes.sectionTitle}>
            <Description style={{ fontSize: 14, paddingRight: 5 }} /> Certificate templates
          </Typography>
          <Typography variant='body2' className={classes.sectionDescription}>
            One template can be used by many levels. Volunteers download their certificates as PDFs.
          </Typography>

          {templates.map((template) => {
            const draft = draftFromTemplate(template);
            return (
              <Box key={template.certificateTemplateId} className={classes.row}>
                <Box className={classes.rowHead}>
                  <Typography className={classes.name}>{template.name}</Typography>
                  <CertificateTemplateMenuButton
                    onAction={(action) => (action === 'edit' ? setEditorDraft(draft) : setDeleting(template))}
                  />
                </Box>
                <Typography className={classes.description}>{describeTemplate(draft)}</Typography>
                <Box className={classes.chips}>
                  <Chip
                    className={classes.chip}
                    label={
                      template.levelCount
                        ? `Used by ${template.levelCount} level${template.levelCount === 1 ? '' : 's'}`
                        : 'Not used by any levels'
                    }
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {editorDraft && (
        <CertificateTemplateDialog
          open
          initialDraft={editorDraft}
          saving={saveFetch.isLoading}
          onClose={() => setEditorDraft(undefined)}
          onSave={onSave}
        />
      )}

      <ConfirmationDialog
        title='Delete template'
        body={`Delete "${deleting?.name ??
          ''}"? Levels using it stop issuing certificates. Certificates already issued can still be downloaded.`}
        open={Boolean(deleting)}
        closeActionText='Cancel'
        confirmActionText='Delete'
        handleCloseClick={() => setDeleting(undefined)}
        handleConfirmClick={onDeleteConfirm}
      />
    </Box>
  );
};
